import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTickets } from '../../hooks/useTickets';
import { useAuth } from '../../hooks/useAuth';
import { useTicketChat } from '../../hooks/useTicketChat';
import { useToast } from '../../hooks/useToast';
import {
  runNeo,
  resumeNeo,
  resetNeo,
  rejectTicket,
  chatWithOski,
  updateTicketContext,
  updateTicketStatus,
  sendToPame,
  listNeoRuns,
  answerNeoRescue,
  type ArchivedNeoRun,
} from '../../lib/api';
import type { Ticket, TicketStatus, ChatMessage } from '../../types';
import styles from './KanbanBoard.module.css';

// Mapa de ID de app → label visible (debe coincidir con APP_DEFAULTS del backend)
const APP_LABELS: Record<string, string> = {
  broota: 'Broota',
  xbroota: 'Xbroota',
  nalca: 'Nalca',
  dully: 'Dully',
  'team-ticket': 'Oskiapp',
  general: 'General',
};

// ─── Tipo del job de Neo (espejado desde Firestore neo_jobs/{notionId}) ──────
type NeoJobState =
  | 'idle' | 'running' | 'done' | 'error'
  | 'awaiting_clarification' | 'manual_review';

type NeoFailure = {
  category: 'pame_rescue' | 'neo_retry' | 'hard_fail';
  reason:   string;
  phase?:   string;
  occurredAt?: string;
};

type NeoJob = {
  state: NeoJobState;
  log: string[];
  prUrl: string;
  branchName?: string;
  filesChanged?: number;
  filePaths?: string[];
  error?: string;
  failure?: NeoFailure | null;
  rescues?: {
    count:   number;
    history: Array<{ askedAt: string; reason: string; category: string }>;
  };
  resume?: { count: number; lastResumedAt?: string };
  checkpoint?: {
    changedFiles: Array<{ path: string; content: string; explanation?: string; isNew?: boolean }>;
    ticket: unknown;
    analysis: unknown;
    savedAt: string;
  };
  checkpointSize?: number;
  checkpointTooLarge?: boolean;
};

// ─── Pipeline de estados ──────────────────────────────────────────────────
const PIPELINE: { label: string; statuses: string[]; icon: string }[] = [
  { label: 'Creada', statuses: ['created', 'received'], icon: '📋' },
  {
    label: 'Evaluando',
    statuses: ['evaluating', 'pame-progress', 'neo-ready', 'triage', 'with-ai'],
    icon: '🤖',
  },
  {
    label: 'Desarrollo',
    statuses: ['development', 'neo-progress', 'in-progress'],
    icon: '🔨',
  },
  { label: 'Revisión', statuses: ['review'], icon: '🔎' },
  { label: 'Staging', statuses: ['staging'], icon: '🧪' },
  { label: 'Producción', statuses: ['production', 'done'], icon: '🚀' },
];

// ─── Columnas del Kanban ──────────────────────────────────────────────────
type Column = {
  key: string;
  statuses: TicketStatus[];
  label: string;
  color: string;
  highlight?: boolean;
  hint?: string;
};

const COLUMNS: Column[] = [
  {
    key: 'created',
    statuses: ['created', 'received'],
    label: 'Creada',
    color: 'var(--muted)',
    hint: 'Esperando aprobación para Pame',
  },
  {
    key: 'evaluating',
    statuses: ['evaluating', 'pame-progress', 'neo-ready', 'triage', 'with-ai'],
    label: 'Evaluando',
    color: 'var(--dully)',
    hint: 'Pame analizando — DEV/SYS puede enviar a Neo',
  },
  {
    key: 'development',
    statuses: ['development', 'neo-progress', 'in-progress'],
    label: 'Desarrollo',
    color: 'var(--dully)',
    hint: 'Neo implementando',
  },
  {
    key: 'review',
    statuses: ['review'],
    label: 'Revisión',
    color: 'var(--dully)',
    hint: 'PR abierto — merge a staging para avanzar',
  },
  {
    key: 'staging',
    statuses: ['staging'],
    label: '🧪 Staging',
    color: 'var(--xbroota)',
    highlight: true,
    hint: 'Listo para probar en staging',
  },
  {
    key: 'production',
    statuses: ['production', 'done'],
    label: '🚀 Producción',
    color: '#f97316',
    highlight: true,
    hint: 'Está live',
  },
];

// Estados canónicos — los únicos que aparecen en el selector de estado
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'created', label: '📋 Creada' },
  { value: 'evaluating', label: '🤖 Evaluando' },
  { value: 'development', label: '🔨 Desarrollo' },
  { value: 'review', label: '🔎 Revisión' },
  { value: 'staging', label: '🧪 Staging' },
  { value: 'production', label: '🚀 Producción' },
  { value: 'rejected', label: '❌ Rechazado' },
];

// Mapa completo (incluye legacy) solo para mostrar labels en cards y badges
const STATUS_LABELS: Record<string, string> = {
  created: '📋 Creada',
  evaluating: '🤖 Evaluando',
  development: '🔨 Desarrollo',
  review: '🔎 Revisión',
  staging: '🧪 Staging',
  production: '🚀 Producción',
  rejected: '❌ Rechazado',
  // Legacy — solo lectura, no aparecen en el selector
  received: '📋 Creada',
  triage: '🤖 Evaluando',
  'with-ai': '🤖 Evaluando',
  'in-progress': '🔨 Desarrollo',
  'pame-progress': '🤖 Evaluando',
  'neo-ready': '🤖 Evaluando',
  'neo-progress': '🔨 Desarrollo',
  done: '🚀 Producción',
  discarded: '❌ Rechazado',
};

const PRIO_LABELS: Record<Ticket['priority'], string> = {
  critical: '🔴 Urgente',
  high: '🟠 Alta',
  medium: '🟡 Normal',
  low: '🟢 Cuando pueda',
};

const PRIO_COLORS: Record<Ticket['priority'], string> = {
  critical: 'var(--danger)',
  high: '#f97316',
  medium: 'var(--warning)',
  low: 'var(--nalca)',
};

// ─── Modal de detalle ──────────────────────────────────────────────────────
function TicketDetail({
  ticket,
  onClose,
}: {
  ticket: Ticket;
  onClose: () => void;
}) {
  const [showTech, setShowTech] = useState(false);
  const [showConvo, setShowConvo] = useState(false);
  const [showNeoLog, setShowNeoLog] = useState(
    !ticket.githubIssueUrl &&
      ['development', 'neo-progress', 'in-progress'].includes(ticket.status),
  );
  const [ticketStatus, setTicketStatus] = useState<TicketStatus>(ticket.status);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const { teamUser, isSysadmin, isDev, isSenior, isJunior } = useAuth();
  const { showToast } = useToast();
  const {
    chat,
    loading: chatLoading,
    loaded: chatLoaded,
    loadChat,
    appendMessages,
  } = useTicketChat();

  // ── Chat continuation state ──────────────────────────────────────────────
  const [sessionMessages, setSessionMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(''); // '' | status text
  const [contextInjected, setContextInjected] = useState(false);

  // Pame context-update banner
  const [pendingContext, setPendingContext] = useState<string | null>(null);
  const [contextUpdating, setContextUpdating] = useState(false);
  const [contextUpdated, setContextUpdated] = useState(false);

  // Rechazo
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [rejectClosePr, setRejectClosePr] = useState(true);
  const [rejecting, setRejecting] = useState(false);
  const [rejected, setRejected] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load chat when modal opens
  useEffect(() => {
    loadChat(ticket.notionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.notionId]);

  // Cerrar modal con Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat?.thread, sessionMessages, chatSending]);

  // ── Neo state — sincronizado con Firestore neo_jobs/{notionId} ──────────────
  const initialNeoState = ticket.githubIssueUrl
    ? 'done'
    : ['development', 'neo-progress', 'in-progress'].includes(
          ticket.status,
        )
      ? 'running'
      : 'idle';

  const [neoState, setNeoState] =
    useState<NeoJobState>(initialNeoState);
  const [neoStatus, setNeoStatus] = useState('');
  const [prUrl, setPrUrl] = useState(ticket.githubIssueUrl ?? '');
  const [neoLog, setNeoLog] = useState<string[]>([]);
  const [neoBranch, setNeoBranch] = useState('');
  const [neoFiles, setNeoFiles] = useState(0);
  const [neoFilePaths, setNeoFilePaths] = useState<string[]>([]);
  const [neoSending, setNeoSending] = useState(false);
  const [neoCheckpointAvailable, setNeoCheckpointAvailable] = useState(false);
  const [neoFailure, setNeoFailure] = useState<NeoFailure | null>(null);
  const [neoRescues, setNeoRescues] = useState<NeoJob['rescues']>(undefined);
  // Hidratación: false hasta recibir el primer snapshot de Firestore. Evita
  // mostrar placeholders falsos ("Iniciando…") mientras la conexión arranca.
  const [neoHydrated, setNeoHydrated] = useState(false);
  const [archivedRuns, setArchivedRuns] = useState<ArchivedNeoRun[]>([]);
  const [selectedArchivedRun, setSelectedArchivedRun] =
    useState<ArchivedNeoRun | null>(null);
  // Ref para bloquear que Firestore sobreescriba el log mientras SSE está activo
  const sseActiveRef = useRef(false);

  // ── onSnapshot: fuente de verdad para estado persistido y reconexión ────────
  useEffect(() => {
    const ref = doc(db, 'neo_jobs', ticket.notionId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setNeoHydrated(true);
        if (!snap.exists()) {
          if (!sseActiveRef.current) setNeoLog([]);
          setNeoState('idle');
          return;
        }
        const job = snap.data() as NeoJob;
        const state = job.state ?? 'idle';
        setNeoState(state);
        // Solo actualizar el log desde Firestore si SSE no está activo
        // (evita sobreescribir el log en tiempo real con snapshots retrasados)
        if (!sseActiveRef.current) setNeoLog(job.log ?? []);
        // currentStep: preferir error explícito, sino el último log entry
        setNeoStatus(job.error || job.log?.at(-1) || '');
        if (job.prUrl) setPrUrl(job.prUrl);
        if (job.branchName) setNeoBranch(job.branchName);
        if (job.filesChanged) setNeoFiles(job.filesChanged);
        if (job.filePaths) setNeoFilePaths(job.filePaths);
        setNeoFailure(job.failure ?? null);
        setNeoRescues(job.rescues);
        setNeoCheckpointAvailable(
          Array.isArray(job.checkpoint?.changedFiles) && job.checkpoint.changedFiles.length > 0,
        );
        // Expandir log automáticamente cuando Neo arranca o termina con error
        if (state === 'running' || state === 'error')
          setShowNeoLog(true);
        // Sincronizar spinner del Kanban card
        window.dispatchEvent(
          new CustomEvent('neo-state-change', {
            detail: { notionId: ticket.notionId, state },
          }),
        );
        if (state === 'done' || state === 'error') {
          window.dispatchEvent(new CustomEvent('ticket-created'));
        }
      },
      (err) => {
        // Sin error handler, una falla silenciosa de Firestore (reglas, red)
        // deja neoHydrated=false para siempre → UI colgada en "Cargando log…".
        console.error(
          '[KanbanBoard] onSnapshot neo_jobs error:',
          err,
        );
        setNeoHydrated(true);
      },
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.notionId]);

  // ── Runs archivados (subcollection runs/) — recarga al cambiar de estado ───
  useEffect(() => {
    if (!isSysadmin && !isDev) return;
    let cancelled = false;
    listNeoRuns(ticket.notionId)
      .then((runs) => {
        if (!cancelled) setArchivedRuns(runs);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Recargar cuando Neo termina o se resetea (aparece un nuevo run archivado)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.notionId, neoState, isSysadmin, isDev]);

  // Backend hace `set({...}, { merge: false })` al iniciar/resetear, lo que wipea
  // job.log mientras el run anterior queda copiado en /runs/. Si el usuario
  // re-abre el ticket sin SSE activo, el snapshot lo deja con neoLog=[] y la
  // "ventana del último run" desaparece. Fallback: mostrar el archivado más
  // reciente cuando neoLog está vacío.
  const lastArchived = archivedRuns[0];
  const effectiveLog =
    neoLog.length > 0 ? neoLog : (lastArchived?.log ?? []);
  const effectiveLogIsArchived =
    neoLog.length === 0 && (lastArchived?.log?.length ?? 0) > 0;
  const archivedLabel = useMemo(() => {
    if (!effectiveLogIsArchived || !lastArchived?.archivedAt) return '';
    return new Date(lastArchived.archivedAt).toLocaleString('es-CL', {
      day:    '2-digit',
      month:  'short',
      hour:   '2-digit',
      minute: '2-digit',
    });
  }, [effectiveLogIsArchived, lastArchived?.archivedAt]);

  // Enviar a Pame: SR/DEV/SYS cuando el ticket está en 'created'
  const canSendToPame =
    (isSysadmin || isDev || isSenior) &&
    ['created', 'received'].includes(ticket.status);

  // Enviar a Neo: DEV/SYS cuando el ticket está en 'evaluating' y tiene contexto técnico
  const canUseNeo =
    (isSysadmin || isDev) &&
    !!ticket.technicalContext &&
    [
      'evaluating',
      'pame-progress',
      'neo-ready',
      'with-ai',
      'development',
      'neo-progress',
      'in-progress',
    ].includes(ticket.status);

  // Rechazar: SR/DEV/SYS en cualquier estado activo
  const canReject =
    (isSysadmin || isDev || isSenior) &&
    !['rejected', 'discarded', 'production', 'done'].includes(
      ticket.status,
    );

  async function handleNeo() {
    setNeoSending(true);
    setNeoState('running');
    setNeoLog([]);
    setShowNeoLog(true);
    setTicketStatus('development');
    window.dispatchEvent(new CustomEvent('ticket-created'));
    sseActiveRef.current = true;
    try {
      await runNeo(ticket.notionId, (text) => {
        setNeoLog((prev) => [...prev, text]);
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setNeoState('error');
      setNeoStatus(msg);
      showToast(`Error al iniciar Neo: ${msg}`, 'error');
    } finally {
      sseActiveRef.current = false;
      setNeoSending(false);
    }
  }

  async function handleNeoReset() {
    try {
      await resetNeo(ticket.notionId);
      setNeoState('idle');
      setNeoStatus('');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Error al resetear Neo';
      showToast(msg, 'error');
    }
  }

  // Reanuda un job fallido post-Kika desde el checkpoint persistido.
  // Sólo re-hace el push a GitHub (branch + commits + PR) sin re-llamar Opus/Kika.
  async function handleNeoResume() {
    setNeoSending(true);
    setNeoState('running');
    setShowNeoLog(true);
    sseActiveRef.current = true;
    try {
      await resumeNeo(ticket.notionId, (text) => {
        setNeoLog((prev) => [...prev, text]);
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setNeoState('error');
      setNeoStatus(msg);
      showToast(`Error al reanudar Neo: ${msg}`, 'error');
    } finally {
      sseActiveRef.current = false;
      setNeoSending(false);
    }
  }

  // ── Chat send ──────────────────────────────────────────────────────────────
  async function handleSendChat() {
    const raw = chatInput.trim();
    if (!raw || chatSending) return;

    // State-aware routing: si Neo está awaiting_clarification, la respuesta del
    // user va al endpoint /neo/answer-rescue en vez de chatWithOski. El backend
    // posteará el mensaje en ticket_chats.thread y reanudará Neo con la
    // aclaración inyectada en technicalContext.
    if (neoState === 'awaiting_clarification') {
      setChatSending('Reanudando Neo con tu aclaración...');
      const userMsg: ChatMessage = {
        id: `user-rescue-${Date.now()}`,
        role: 'user',
        content: raw,
        timestamp: new Date().toISOString(),
      };
      setSessionMessages((prev) => [...prev, userMsg]);
      setChatInput('');
      sseActiveRef.current = true;
      try {
        await answerNeoRescue(ticket.notionId, raw, (text) => {
          setNeoLog((prev) => [...prev, text]);
          setChatSending(text);
        });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        showToast(`Error reanudando Neo: ${msg}`, 'error');
      } finally {
        sseActiveRef.current = false;
        setChatSending('');
      }
      return;
    }

    const userId = teamUser?.uid ?? 'unknown';

    // Build the message to send — inject ticket context if first message of session
    let messageToSend = raw;
    const hasHistory =
      (chat?.origin?.length ?? 0) + (chat?.thread?.length ?? 0) > 0 ||
      sessionMessages.length > 0;
    if (!contextInjected && !hasHistory) {
      messageToSend = `[Contexto del ticket ${ticket.id}: "${ticket.title}"
App: ${ticket.apps?.join(', ') ?? 'N/A'} | Estado: ${ticket.status} | Prioridad: ${ticket.priority ?? 'N/A'}
Resumen: ${ticket.description ?? 'Sin resumen'}
Contexto técnico (Pame): ${ticket.technicalContext ?? 'No disponible'}]

${raw}`;
      setContextInjected(true);
    }

    // Build history from origin + thread + session messages
    const history: ChatMessage[] = [
      ...(chat?.origin ?? []),
      ...(chat?.thread ?? []),
      ...sessionMessages,
    ];

    // Optimistically add user message to session
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: raw, // show raw (without injected context) in UI
      timestamp: new Date().toISOString(),
    };
    setSessionMessages((prev) => [...prev, userMsg]);
    setChatInput('');
    setChatSending('Enviando...');

    try {
      const response = await chatWithOski(
        { message: messageToSend, history, userId },
        (statusText) => setChatSending(statusText),
      );

      const agentMsg: ChatMessage = {
        id: `agent-${Date.now()}`,
        role: 'assistant',
        content: response.reply,
        agent: response.agent,
        timestamp: new Date().toISOString(),
      };

      setSessionMessages((prev) => [...prev, agentMsg]);
      setChatSending('');

      // Persist both messages to Firestore
      await appendMessages(ticket.notionId, [userMsg, agentMsg]);

      // Check if Pame proposes updating the technical context
      if (
        response.action === 'create-request' &&
        response.suggestedRequest?.technicalContext
      ) {
        setPendingContext(
          response.suggestedRequest.technicalContext,
        );
        setContextUpdated(false);
      }
    } catch (err: unknown) {
      setChatSending('');
      const errMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: `Error: ${err instanceof Error ? err.message : 'Error desconocido'}`,
        agent: 'oski',
        timestamp: new Date().toISOString(),
      };
      setSessionMessages((prev) => [...prev, errMsg]);
    }
  }

  async function handleUpdateContext() {
    if (!pendingContext) return;
    setContextUpdating(true);
    try {
      await updateTicketContext(ticket.notionId, pendingContext);
      setContextUpdated(true);
    } catch (err) {
      showToast(
        'Error al actualizar el contexto técnico',
        'error',
      );
    } finally {
      setContextUpdating(false);
    }
  }

  async function handleReject() {
    setRejecting(true);
    try {
      await rejectTicket(
        ticket.notionId,
        rejectClosePr,
        prUrl || undefined,
      );
      setRejected(true);
      setTicketStatus('discarded');
      setShowRejectConfirm(false);
      window.dispatchEvent(new CustomEvent('ticket-created'));
      // Cerrar el modal automáticamente después de 1.5 segundos
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      showToast('Error al rechazar el ticket', 'error');
    } finally {
      setRejecting(false);
    }
  }

  // Orden cronológico: mensajes de origen → thread archivado → sesión actual
  const allMessages = [
    ...(chat?.origin ?? []),
    ...(chat?.thread ?? []),
    ...sessionMessages,
  ];

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  // ── Label de autor para mensajes de conversación ──────────────────────────
  // Usa msg.authorName si está disponible (mensajes de origen con autor explícito),
  // luego el requestedBy del ticket para mensajes de usuario sin autor,
  // luego el nombre del agente para mensajes de assistant.
  function getAuthorLabel(msg: ChatMessage): string {
    if (msg.role === 'user') {
      return (
        (msg as ChatMessage & { authorName?: string }).authorName ||
        ticket.requestedBy ||
        teamUser?.displayName ||
        'Usuario'
      );
    }
    const agent = msg.agent ?? 'oski';
    return agent.charAt(0).toUpperCase() + agent.slice(1);
  }

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={ticket.title}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalMeta}>
            <span className={styles.modalId}>{ticket.id}</span>
            <span className={styles.modalStatus}>
              {STATUS_LABELS[ticket.status] ?? ticket.status}
            </span>
            {ticket.agentName && (
              <span className={styles.modalAgent}>
                {ticket.agentName}
              </span>
            )}
            {ticket.requestedBy && (
              <span
                className={styles.modalRequester}
                title="Solicitante"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                </svg>
                {ticket.requestedBy}
              </span>
            )}
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <h2 className={styles.modalTitle}>{ticket.title}</h2>

        {/* Pipeline de progreso */}
        {(() => {
          const activeIdx = PIPELINE.findIndex((s) =>
            s.statuses.includes(ticket.status),
          );
          return (
            <div className={styles.pipeline}>
              {PIPELINE.map((step, i) => {
                const done = i < activeIdx;
                const active = i === activeIdx;
                const pending = i > activeIdx;
                return (
                  <div key={step.label} className={styles.pipelineStep}>
                    {i > 0 && (
                      <div
                        className={`${styles.pipelineLine} ${
                          done || active
                            ? styles.pipelineLineDone
                            : ''
                        }`}
                      />
                    )}
                    <div
                      className={`${styles.pipelineNode} ${
                        active
                          ? styles.pipelineNodeActive
                          : done
                            ? styles.pipelineNodeDone
                            : styles.pipelineNodePending
                      }`}
                    >
                      {done ? '✓' : step.icon}
                    </div>
                    <span
                      className={`${styles.pipelineLabel} ${
                        pending
                          ? styles.pipelineLabelFaint
                          : active
                            ? styles.pipelineLabelActive
                            : ''
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Pills: apps + tipo + prioridad */}
        <div className={styles.modalPills}>
          {ticket.apps.length > 0 ? (
            ticket.apps.map((app) => (
              <span key={app} className={`pill pill-${app}`}>
                {APP_LABELS[app] ?? app.toUpperCase()}
              </span>
            ))
          ) : (
            <span className={styles.typePill}>General</span>
          )}
          <span className={styles.typePill}>{ticket.type}</span>
          <span
            className={styles.prioPill}
            style={{
              color: PRIO_COLORS[ticket.priority],
              borderColor: PRIO_COLORS[ticket.priority],
            }}
          >
            {PRIO_LABELS[ticket.priority]}
          </span>
        </div>

        {/* Cuerpo */}
        <div className={styles.modalBody}>
          {/* Banner de acción según estado */}
          {ticket.status === 'review' && (
            <div
              className={styles.actionBanner}
              style={{ borderColor: 'var(--dully)' }}
            >
              <span className={styles.actionIcon}>🔎</span>
              <div>
                <div style={{ fontWeight: 500, marginBottom: 2 }}>
                  PR listo para revisar
                </div>
                <div style={{ opacity: 0.8, fontSize: 11 }}>
                  Revisá los cambios de Neo en GitHub y mergeá cuando
                  estés conforme.
                </div>
              </div>
            </div>
          )}
          {ticket.status === 'staging' && (
            <div
              className={styles.actionBanner}
              style={{ borderColor: 'var(--xbroota)' }}
            >
              <span className={styles.actionIcon}>🧪</span>
              <div>
                <div style={{ fontWeight: 500, marginBottom: 2 }}>
                  Desplegado en staging
                </div>
                <div style={{ opacity: 0.8, fontSize: 11 }}>
                  Probá que todo funcione antes de pasar a producción.
                </div>
              </div>
            </div>
          )}
          {ticket.status === 'production' && (
            <div
              className={styles.actionBanner}
              style={{ borderColor: '#f97316' }}
            >
              <span className={styles.actionIcon}>🚀</span>
              <div>
                <div style={{ fontWeight: 500, marginBottom: 2 }}>
                  En producción
                </div>
                <div style={{ opacity: 0.8, fontSize: 11 }}>
                  Confirmá que todo funciona correctamente en prod.
                </div>
              </div>
            </div>
          )}

          {/* Resumen */}
          {ticket.description && (
            <section className={styles.modalSection}>
              <div className={styles.sectionLabel}>Resumen</div>
              <div className={styles.sectionText}>
                {ticket.description}
              </div>
            </section>
          )}

          {/* Creado por + fechas */}
          <section className={styles.modalSection}>
            <div className={styles.sectionLabel}>Info</div>
            <div className={styles.infoGrid}>
              {ticket.requestedBy && (
                <div className={styles.infoItem}>
                  <span className={styles.infoKey}>Solicitante</span>
                  <span className={styles.infoVal}>
                    {ticket.requestedBy}
                  </span>
                </div>
              )}
              <div className={styles.infoItem}>
                <span className={styles.infoKey}>Creado</span>
                <span className={styles.infoVal}>
                  {fmt(ticket.createdAt)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoKey}>Actualizado</span>
                <span className={styles.infoVal}>
                  {fmt(ticket.updatedAt)}
                </span>
              </div>
              {isSysadmin && (
                <div className={styles.infoItem}>
                  <span className={styles.infoKey}>Estado</span>
                  <select
                    className={styles.statusSelect}
                    value={ticketStatus}
                    disabled={statusUpdating}
                    onChange={async (e) => {
                      const next = e.target.value as TicketStatus;
                      setStatusUpdating(true);
                      try {
                        await updateTicketStatus(
                          ticket.notionId,
                          next,
                        );
                        setTicketStatus(next);
                        // Refrescar el listado para que el card se mueva de columna
                        window.dispatchEvent(
                          new CustomEvent('ticket-created'),
                        );
                      } finally {
                        setStatusUpdating(false);
                      }
                    }}
                  >
                    {STATUS_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </section>

          {/* ── Aprobar: enviar a Pame (SR/DEV/SYS cuando ticket está en 'created') ── */}
          {canSendToPame && (
            <section className={styles.modalSection}>
              <button
                className={styles.sendToPameBtn}
                onClick={async () => {
                  try {
                    await sendToPame(ticket.notionId);
                    setTicketStatus('evaluating');
                    window.dispatchEvent(
                      new CustomEvent('ticket-created'),
                    );
                  } catch (err) {
                    showToast('Error al enviar a Pame', 'error');
                  }
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Aprobar y enviar a Pame
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </section>
          )}

          {/* ── Rechazar ticket ──────────────────────────────────────────── */}
          {canReject && !rejected && (
            <section className={styles.modalSection}>
              {!showRejectConfirm ? (
                <button
                  className={styles.rejectBtn}
                  onClick={() => setShowRejectConfirm(true)}
                >
                  ✕ Rechazar ticket
                </button>
              ) : (
                <div className={styles.rejectConfirm}>
                  <p className={styles.rejectConfirmTitle}>
                    ¿Rechazar este ticket?
                  </p>
                  {prUrl && (
                    <label className={styles.rejectCheckbox}>
                      <input
                        type="checkbox"
                        checked={rejectClosePr}
                        onChange={(e) =>
                          setRejectClosePr(e.target.checked)
                        }
                      />
                      Cerrar PR en GitHub
                    </label>
                  )}
                  <div className={styles.rejectActions}>
                    <button
                      className={styles.rejectCancelBtn}
                      onClick={() => setShowRejectConfirm(false)}
                      disabled={rejecting}
                    >
                      Cancelar
                    </button>
                    <button
                      className={styles.rejectConfirmBtn}
                      onClick={handleReject}
                      disabled={rejecting}
                    >
                      {rejecting
                        ? 'Rechazando…'
                        : 'Confirmar rechazo'}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ── Ticket rechazado — crear nuevo ───────────────────────────── */}
          {rejected && (
            <section className={styles.modalSection}>
              <div className={styles.rejectedCard}>
                <span className={styles.rejectedIcon}>✕</span>
                <span className={styles.rejectedTitle}>
                  Ticket rechazado
                </span>
                <button
                  className={styles.rejectedNewBtn}
                  onClick={() => {
                    onClose();
                    // Pre-llenar el chat de Oski con contexto del ticket rechazado
                    window.dispatchEvent(
                      new CustomEvent('oski-prefill', {
                        detail: {
                          message:
                            `Quiero crear un nuevo ticket basado en el rechazado: "${ticket.title}". ${ticket.description ?? ''}`.trim(),
                        },
                      }),
                    );
                  }}
                >
                  + Crear nuevo ticket
                </button>
              </div>
            </section>
          )}

          {/* ── Conversación + mini-chat ─────────────────────────────────── */}
          <section className={styles.modalSection}>
            <button
              className={styles.techToggle}
              onClick={() => setShowConvo((v) => !v)}
              aria-expanded={showConvo}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <polyline
                  points={
                    showConvo
                      ? '18 15 12 9 6 15'
                      : '6 9 12 15 18 9'
                  }
                />
              </svg>
              Conversación
            </button>

            {showConvo && (
              <div className={styles.ticketChat}>
                {/* Messages area — scroll interno + redimensionable verticalmente */}
                <div className={styles.ticketChatMessages}>
                  {chatLoading && (
                    <p
                      className={styles.techText}
                      style={{ opacity: 0.5, padding: '8px 10px' }}
                    >
                      Cargando conversación...
                    </p>
                  )}

                  {chatLoaded && allMessages.length === 0 && (
                    <p className={styles.ticketChatEmpty}>
                      Sin conversación archivada — iniciá una abajo.
                    </p>
                  )}

                  {allMessages.map((msg, i) => (
                    <div
                      key={msg.id ?? i}
                      className={`${styles.convoMsg} ${
                        msg.role === 'user'
                          ? styles.convoUser
                          : styles.convoAgent
                      }`}
                    >
                      <span className={styles.convoRole}>
                        {getAuthorLabel(msg)}
                      </span>
                      <span className={styles.convoText}>
                        {msg.content}
                      </span>
                    </div>
                  ))}

                  {/* Typing indicator */}
                  {chatSending && (
                    <div
                      className={`${styles.convoMsg} ${styles.convoAgent}`}
                    >
                      <span className={styles.convoRole}>
                        Escribiendo...
                      </span>
                      <span
                        className={`${styles.convoText} ${styles.convoTyping}`}
                      >
                        <span className={styles.typingDots}>
                          <span />
                          <span />
                          <span />
                        </span>
                        {chatSending}
                      </span>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Rescue banner — Pame pidió aclaración, Neo en pausa */}
                {neoState === 'awaiting_clarification' && (() => {
                  const pameMsg = [...(chat?.thread ?? [])]
                    .reverse()
                    .find((m) => m.role === 'assistant' && m.agent === 'pame');
                  const cap = 2;
                  const used = neoRescues?.count ?? 1;
                  return (
                    <div className={styles.rescueBanner}>
                      <div className={styles.rescueBannerHead}>
                        <span className={styles.rescueBannerTag}>
                          ⏸ Neo en pausa · Pame pidió aclaración
                        </span>
                        <span className={styles.rescueBannerCap}>
                          {used}/{cap}
                        </span>
                      </div>
                      {pameMsg?.content && (
                        <div className={styles.rescueBannerQuestion}>
                          {pameMsg.content}
                        </div>
                      )}
                      <div className={styles.rescueBannerHint}>
                        Responde abajo y Neo va a reanudar con tu aclaración.
                      </div>
                    </div>
                  );
                })()}

                {/* Manual review banner — cap exhausted o hard_fail */}
                {neoState === 'manual_review' && (
                  <div className={styles.manualReviewBanner}>
                    <span className={styles.manualReviewIcon}>⚠️</span>
                    <div className={styles.manualReviewBody}>
                      <div className={styles.manualReviewTitle}>
                        Manual review · Neo no pudo continuar
                      </div>
                      {neoFailure?.reason && (
                        <div className={styles.manualReviewReason}>
                          {neoFailure.reason}
                        </div>
                      )}
                      <div className={styles.manualReviewHint}>
                        Sysadmin/Dev: revisa el log archivado y resetea si corresponde para volver a correr Neo.
                      </div>
                      {(isSysadmin || isDev) && (
                        <div className={styles.neoErrorActions}>
                          <button
                            className={styles.neoRetryBtn}
                            disabled={neoSending}
                            onClick={async () => {
                              await handleNeoReset();
                              setNeoLog([]);
                              setNeoStatus('');
                            }}
                          >
                            Reset (re-correr completo)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pame context banner */}
                {pendingContext && !contextUpdated && (
                  <div className={styles.contextBanner}>
                    <span className={styles.contextBannerIcon}>
                      ✨
                    </span>
                    <span className={styles.contextBannerText}>
                      Pame propone actualizar el contexto técnico
                    </span>
                    <button
                      className={styles.contextBannerBtn}
                      onClick={handleUpdateContext}
                      disabled={contextUpdating}
                    >
                      {contextUpdating
                        ? 'Actualizando...'
                        : 'Actualizar en Notion'}
                    </button>
                  </div>
                )}
                {contextUpdated && (
                  <div className={styles.contextBannerDone}>
                    Contexto actualizado ✅ — puedes enviar a Neo
                  </div>
                )}

                {/* Input row */}
                <div className={styles.ticketChatInput}>
                  <textarea
                    className={styles.ticketChatTextarea}
                    placeholder={
                      neoState === 'awaiting_clarification'
                        ? 'Responde la pregunta de Pame para que Neo reanude...'
                        : 'Pregunta sobre este ticket...'
                    }
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendChat();
                      }
                    }}
                    disabled={!!chatSending}
                    rows={2}
                  />
                  <button
                    className={styles.ticketChatSendBtn}
                    onClick={handleSendChat}
                    disabled={!!chatSending || !chatInput.trim()}
                    aria-label="Enviar"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Contexto técnico */}
          {ticket.technicalContext && (
            <section className={styles.modalSection}>
              <button
                className={styles.techToggle}
                onClick={() => setShowTech((v) => !v)}
                aria-expanded={showTech}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <polyline
                    points={
                      showTech
                        ? '18 15 12 9 6 15'
                        : '6 9 12 15 18 9'
                    }
                  />
                </svg>
                Contexto técnico
              </button>
              {showTech && (
                <div className={styles.techContent}>
                  <pre className={styles.techText}>
                    {ticket.technicalContext}
                  </pre>
                </div>
              )}
            </section>
          )}

          {/* PR link (cuando no hay sesión de Neo activa pero el ticket ya tiene PR) */}
          {ticket.githubIssueUrl && !canUseNeo && (
            <section className={styles.modalSection}>
              <div className={styles.sectionLabel}>
                Pull Request
              </div>
              <a
                href={ticket.githubIssueUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkBtn}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                </svg>
                Ver PR en GitHub
              </a>
            </section>
          )}
        </div>

        {/* Neo */}
        {canUseNeo && (
          <div className={styles.neoSection}>
            {/* IDLE — invitación a ejecutar */}
            {neoState === 'idle' && (
              <div className={styles.neoIdle}>
                <div className={styles.neoIdleInfo}>
                  <span className={styles.neoIdleIcon}>⬡</span>
                  <div>
                    <div className={styles.neoIdleTitle}>
                      Listo para Neo
                    </div>
                    <div className={styles.neoIdleDesc}>
                      Va a crear un branch, commitear los cambios y
                      abrir un PR en GitHub.
                    </div>
                  </div>
                </div>
                <button
                  className={styles.neoBtn}
                  onClick={handleNeo}
                  disabled={neoSending}
                >
                  {neoSending ? 'Iniciando…' : 'Enviar a Neo →'}
                </button>
              </div>
            )}

            {/* RUNNING — log de progreso */}
            {neoState === 'running' && (
              <div className={styles.neoRunningCard}>
                <div className={styles.neoRunningHeader}>
                  <span className={styles.neoPulse} />
                  <span className={styles.neoRunningTitle}>
                    Neo trabajando…
                  </span>
                  {(isSysadmin || isDev) && (
                    <>
                      <button
                        className={styles.neoLogToggle}
                        onClick={() => setShowNeoLog((v) => !v)}
                      >
                        {showNeoLog ? 'Ocultar log' : 'Ver log'}
                      </button>
                      <button
                        className={styles.neoResetBtn}
                        onClick={handleNeoReset}
                        title="Resetear job pegado"
                      >
                        ↺ Reset
                      </button>
                    </>
                  )}
                </div>
                {/* Log expandido — estilo terminal */}
                {showNeoLog && (
                  <div className={styles.neoTerminal}>
                    {effectiveLogIsArchived && (
                      <div className={styles.neoArchivedBanner}>
                        Mostrando run anterior · {archivedLabel}
                      </div>
                    )}
                    {!neoHydrated ? (
                      <div className={styles.neoTerminalLine}>
                        <span
                          className={styles.neoTerminalPrompt}
                        >
                          ▶
                        </span>
                        <span>Cargando log…</span>
                      </div>
                    ) : effectiveLog.length === 0 ? (
                      <div className={styles.neoTerminalLine}>
                        <span
                          className={styles.neoTerminalPrompt}
                        >
                          ▶
                        </span>
                        <span>
                          {neoState === 'running'
                            ? 'Esperando primer estado de Neo…'
                            : 'Sin log para este run.'}
                        </span>
                      </div>
                    ) : (
                      effectiveLog.map((line, i) => (
                        <div
                          key={i}
                          className={`${styles.neoTerminalLine} ${
                            !effectiveLogIsArchived &&
                            i === effectiveLog.length - 1
                              ? styles.neoTerminalCurrent
                              : styles.neoTerminalDone
                          }`}
                        >
                          <span
                            className={styles.neoTerminalPrompt}
                          >
                            {!effectiveLogIsArchived &&
                            i === effectiveLog.length - 1
                              ? '▶'
                              : '✓'}
                          </span>
                          <span>{line}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* DONE — PR card */}
            {neoState === 'done' && (
              <div className={styles.neoDoneCard}>
                <div className={styles.neoDoneHeader}>
                  <span className={styles.neoDoneCheck}>✓</span>
                  <span className={styles.neoDoneTitle}>
                    PR abierto por Neo
                  </span>
                  {(isSysadmin || isDev) && effectiveLog.length > 0 && (
                    <button
                      className={styles.neoLogToggle}
                      onClick={() => setShowNeoLog((v) => !v)}
                    >
                      {showNeoLog ? 'Ocultar log' : 'Ver log'}
                    </button>
                  )}
                </div>
                {showNeoLog && effectiveLog.length > 0 && (
                  <div className={styles.neoTerminal}>
                    {effectiveLogIsArchived && (
                      <div className={styles.neoArchivedBanner}>
                        Mostrando run anterior · {archivedLabel}
                      </div>
                    )}
                    {effectiveLog.map((line, i) => (
                      <div
                        key={i}
                        className={`${styles.neoTerminalLine} ${styles.neoTerminalDone}`}
                      >
                        <span
                          className={styles.neoTerminalPrompt}
                        >
                          ✓
                        </span>
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                )}
                {neoBranch && (
                  <div className={styles.neoDoneMeta}>
                    <span className={styles.neoDoneMetaItem}>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="6" y1="3" x2="6" y2="15" />
                        <circle cx="18" cy="6" r="3" />
                        <circle cx="6" cy="18" r="3" />
                        <path d="M18 9a9 9 0 01-9 9" />
                      </svg>
                      {neoBranch}
                    </span>
                    {neoFiles > 0 && (
                      <span className={styles.neoDoneMetaItem}>
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        {neoFiles} archivo{neoFiles !== 1 ? 's' : ''}
                        {' '}modificado{neoFiles !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
                {neoFilePaths.length > 0 && (
                  <div className={styles.neoDoneFiles}>
                    {neoFilePaths.map((p) => (
                      <span
                        key={p}
                        className={styles.neoDoneFile}
                      >
                        {p.split('/').pop()}
                      </span>
                    ))}
                  </div>
                )}
                <a
                  href={prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.neoPrBtn}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                  </svg>
                  Ver PR en GitHub
                </a>
              </div>
            )}

            {/* ERROR — con reintentar */}
            {neoState === 'error' && (
              <div className={styles.neoErrorCard}>
                <div className={styles.neoErrorHeader}>
                  <span className={styles.neoErrorIcon}>⚠</span>
                  <span className={styles.neoErrorTitle}>
                    Neo no pudo completar
                  </span>
                  {(isSysadmin || isDev) && effectiveLog.length > 0 && (
                    <button
                      className={styles.neoLogToggle}
                      onClick={() => setShowNeoLog((v) => !v)}
                    >
                      {showNeoLog ? 'Ocultar log' : 'Ver log'}
                    </button>
                  )}
                </div>
                {showNeoLog && effectiveLog.length > 0 && (
                  <div className={styles.neoTerminal}>
                    {effectiveLogIsArchived && (
                      <div className={styles.neoArchivedBanner}>
                        Mostrando run anterior · {archivedLabel}
                      </div>
                    )}
                    {effectiveLog.map((line, i) => (
                      <div
                        key={i}
                        className={`${styles.neoTerminalLine} ${
                          !effectiveLogIsArchived &&
                          i === effectiveLog.length - 1
                            ? styles.neoTerminalError
                            : styles.neoTerminalDone
                        }`}
                      >
                        <span
                          className={styles.neoTerminalPrompt}
                        >
                          {!effectiveLogIsArchived &&
                          i === effectiveLog.length - 1
                            ? '✗'
                            : '✓'}
                        </span>
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className={styles.neoErrorMsg}>
                  {neoStatus || 'Sin detalle disponible'}
                </div>
                <div className={styles.neoErrorActions}>
                  {neoCheckpointAvailable && (
                    <button
                      className={styles.neoResumeBtn}
                      disabled={neoSending}
                      title="Reanuda el push a GitHub sin re-correr Opus/Kika. Usalo cuando el error fue post-validación (ej. token de GitHub inválido)."
                      onClick={handleNeoResume}
                    >
                      ↻ Reintentar push a GitHub
                    </button>
                  )}
                  <button
                    className={styles.neoRetryBtn}
                    disabled={neoSending}
                    onClick={async () => {
                      await handleNeoReset();
                      setNeoLog([]);
                      setNeoStatus('');
                    }}
                  >
                    Reset (re-correr completo)
                  </button>
                </div>
              </div>
            )}

            {/* AWAITING_CLARIFICATION — log del run + reset (la pregunta de Pame
                vive en el rescue banner del chat). Sin esto el usuario no veía
                qué pasó ni podía resetear sin responder. */}
            {neoState === 'awaiting_clarification' && (
              <div className={styles.neoErrorCard}>
                <div className={styles.neoErrorHeader}>
                  <span className={styles.neoErrorIcon}>⏸</span>
                  <span className={styles.neoErrorTitle}>
                    Neo en pausa — esperando aclaración
                  </span>
                  {(isSysadmin || isDev) && effectiveLog.length > 0 && (
                    <button
                      className={styles.neoLogToggle}
                      onClick={() => setShowNeoLog((v) => !v)}
                    >
                      {showNeoLog ? 'Ocultar log' : 'Ver log'}
                    </button>
                  )}
                </div>
                {showNeoLog && effectiveLog.length > 0 && (
                  <div className={styles.neoTerminal}>
                    {effectiveLogIsArchived && (
                      <div className={styles.neoArchivedBanner}>
                        Mostrando run anterior · {archivedLabel}
                      </div>
                    )}
                    {effectiveLog.map((line, i) => (
                      <div
                        key={i}
                        className={`${styles.neoTerminalLine} ${styles.neoTerminalDone}`}
                      >
                        <span className={styles.neoTerminalPrompt}>
                          ✓
                        </span>
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                )}
                {neoFailure?.reason && (
                  <div className={styles.neoErrorMsg}>
                    {neoFailure.reason}
                  </div>
                )}
                {(isSysadmin || isDev) && (
                  <div className={styles.neoErrorActions}>
                    <button
                      className={styles.neoRetryBtn}
                      disabled={neoSending}
                      onClick={async () => {
                        await handleNeoReset();
                        setNeoLog([]);
                        setNeoStatus('');
                      }}
                      title="Descarta la pregunta de Pame y vuelve a correr Neo desde cero"
                    >
                      Reset (re-correr completo)
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* MANUAL_REVIEW en neoSection — log + reset/re-run siempre
                visibles para sysadmin/dev. El reset queda fuera del toggle
                del log para que esté accesible incluso con el log contraído. */}
            {neoState === 'manual_review' && (isSysadmin || isDev) && (
              <div className={styles.neoErrorCard}>
                <div className={styles.neoErrorHeader}>
                  <span className={styles.neoErrorIcon}>⚠</span>
                  <span className={styles.neoErrorTitle}>
                    Log del run
                  </span>
                  {effectiveLog.length > 0 && (
                    <button
                      className={styles.neoLogToggle}
                      onClick={() => setShowNeoLog((v) => !v)}
                    >
                      {showNeoLog ? 'Ocultar log' : 'Ver log'}
                    </button>
                  )}
                </div>
                {showNeoLog && effectiveLog.length > 0 && (
                  <div className={styles.neoTerminal}>
                    {effectiveLogIsArchived && (
                      <div className={styles.neoArchivedBanner}>
                        Mostrando run anterior · {archivedLabel}
                      </div>
                    )}
                    {effectiveLog.map((line, i) => (
                      <div
                        key={i}
                        className={`${styles.neoTerminalLine} ${styles.neoTerminalDone}`}
                      >
                        <span className={styles.neoTerminalPrompt}>
                          ✓
                        </span>
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                )}
                {neoFailure?.reason && (
                  <div className={styles.neoErrorMsg}>
                    {neoFailure.reason}
                  </div>
                )}
                <div className={styles.neoErrorActions}>
                  <button
                    className={styles.neoRetryBtn}
                    disabled={neoSending}
                    onClick={async () => {
                      await handleNeoReset();
                      setNeoLog([]);
                      setNeoStatus('');
                    }}
                    title="Resetea el job y vuelve a correr Neo desde cero"
                  >
                    Reset (re-correr completo)
                  </button>
                </div>
              </div>
            )}

            {(isSysadmin || isDev) && archivedRuns.length > 0 && (
              <div className={styles.neoRunsSection}>
                <div className={styles.neoRunsHeader}>
                  Runs anteriores ({archivedRuns.length})
                </div>
                <ul className={styles.neoRunsList}>
                  {archivedRuns.map((run) => {
                    const date = new Date(
                      run.archivedAt,
                    ).toLocaleString('es-CL', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                    const icon =
                      run.state === 'done'
                        ? '✓'
                        : run.state === 'error'
                          ? '✗'
                          : '·';
                    return (
                      <li key={run.archivedAt}>
                        <button
                          className={styles.neoRunItem}
                          onClick={() =>
                            setSelectedArchivedRun(run)
                          }
                          title={`Ver log del run ${date}`}
                        >
                          <span className={styles.neoRunIcon}>
                            {icon}
                          </span>
                          <span className={styles.neoRunDate}>
                            {date}
                          </span>
                          <span className={styles.neoRunState}>
                            {run.state}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {selectedArchivedRun && (
          <div
            className={styles.neoRunModal}
            onClick={() => setSelectedArchivedRun(null)}
          >
            <div
              className={styles.neoRunModalBody}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.neoRunModalHeader}>
                <span>
                  Run archivado —{' '}
                  {new Date(
                    selectedArchivedRun.archivedAt,
                  ).toLocaleString('es-CL')}{' '}
                  · {selectedArchivedRun.state}
                </span>
                <button
                  onClick={() => setSelectedArchivedRun(null)}
                >
                  ✕
                </button>
              </div>
              {selectedArchivedRun.prUrl && (
                <a
                  href={selectedArchivedRun.prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.neoPrBtn}
                >
                  Ver PR en GitHub
                </a>
              )}
              <div className={styles.neoTerminal}>
                {selectedArchivedRun.log.length === 0 ? (
                  <div className={styles.neoTerminalLine}>
                    <span className={styles.neoTerminalPrompt}>
                      ▶
                    </span>
                    <span>Sin log para este run.</span>
                  </div>
                ) : (
                  selectedArchivedRun.log.map((line, i) => (
                    <div
                      key={i}
                      className={`${styles.neoTerminalLine} ${styles.neoTerminalDone}`}
                    >
                      <span className={styles.neoTerminalPrompt}>
                        ✓
                      </span>
                      <span>{line}</span>
                    </div>
                  ))
                )}
              </div>
              {selectedArchivedRun.error && (
                <div className={styles.neoErrorMsg}>
                  {selectedArchivedRun.error}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.modalFooter}>
          <a
            href={`https://www.notion.so/${ticket.notionId.replace(/-/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.notionLink}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z" />
            </svg>
            Ver en Notion
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────
function TicketCard({
  ticket,
  highlight,
  neoState,
  onOpen,
}: {
  ticket: Ticket;
  highlight: boolean;
  neoState: string | undefined;
  onOpen: (t: Ticket) => void;
}) {
  const neoRunning      = neoState === 'running';
  const neoError        = neoState === 'error';
  const neoAwaiting     = neoState === 'awaiting_clarification';
  const neoManualReview = neoState === 'manual_review';
  return (
    <div
      className={`${styles.card} ${highlight ? styles.cardHighlight : ''} ${
        neoRunning ? styles.cardNeoRunning : ''
      } ${neoError ? styles.cardNeoError : ''} ${
        neoAwaiting ? styles.cardNeoAwaiting : ''
      } ${neoManualReview ? styles.cardNeoManualReview : ''}`}
      onClick={() => onOpen(ticket)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(ticket)}
    >
      <div className={styles.cardHeader}>
        <div
          className={styles.cardId}
          style={{ color: 'var(--text)', opacity: 0.75 }}
        >
          {ticket.id}
        </div>
        {neoRunning && (
          <div
            className={styles.cardNeoSpinner}
            title="Neo trabajando..."
          >
            <span className={styles.cardNeoSpinnerDot} />
            <span className={styles.cardNeoSpinnerLabel}>Neo</span>
          </div>
        )}
        {neoAwaiting && (
          <div
            className={styles.cardNeoAwaitingBadge}
            title="Pame pidió aclaración — abrí para responder"
          >
            <span className={styles.cardNeoAwaitingIcon}>⏸</span>
            <span className={styles.cardNeoAwaitingLabel}>Pame?</span>
          </div>
        )}
        {neoManualReview && (
          <div
            className={styles.cardNeoErrorBadge}
            title="Manual review — Neo agotó intentos"
          >
            <span className={styles.cardNeoErrorIcon}>⚠</span>
            <span className={styles.cardNeoErrorLabel}>Review</span>
          </div>
        )}
        {neoError && !neoRunning && !neoAwaiting && !neoManualReview && (
          <div
            className={styles.cardNeoErrorBadge}
            title="Neo falló — abrí para ver el detalle"
          >
            <span className={styles.cardNeoErrorIcon}>⚠</span>
            <span className={styles.cardNeoErrorLabel}>Neo</span>
          </div>
        )}
      </div>
      <div className={styles.cardTitle}>{ticket.title}</div>
      <div className={styles.cardMeta}>
        {ticket.apps.map((app) => (
          <span key={app} className={`pill pill-${app}`}>
            {APP_LABELS[app] ?? app}
          </span>
        ))}
        <span className={styles.typePill}>{ticket.type}</span>
        <span
          className={styles.prioDot}
          style={{ background: PRIO_COLORS[ticket.priority] }}
        />
      </div>
    </div>
  );
}

// ─── Board ─────────────────────────────────────────────────────────────────
export function KanbanBoard() {
  const { tickets, loading, error, fetch, lastUpdated } = useTickets();
  const [searchParams] = useSearchParams();
  const appFilter = searchParams.get('app');
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Map notionId → estado Neo para spinner de cards (se actualiza vía evento)
  const [neoStates, setNeoStates] =
    useState<Record<string, string>>({});

  useEffect(() => {
    const handler = (e: Event) => {
      const { notionId, state } = (e as CustomEvent).detail ?? {};
      if (notionId)
        setNeoStates((prev) => ({ ...prev, [notionId]: state }));
    };
    window.addEventListener('neo-state-change', handler);
    return () =>
      window.removeEventListener('neo-state-change', handler);
  }, []);

  async function handleRefresh() {
    setRefreshing(true);
    await fetch();
    setRefreshing(false);
  }

  if (loading && !lastUpdated)
    return (
      <div className={styles.loading}>Cargando tickets...</div>
    );
  if (error)
    return (
      <div className={styles.error}>
        {error} <button onClick={fetch}>Reintentar</button>
      </div>
    );

  const visibleTickets = appFilter
    ? tickets.filter((t) =>
        t.apps.includes(
          appFilter as import('../../types').AppName,
        ),
      )
    : tickets;

  const byColumn = (col: Column) =>
    visibleTickets.filter((t) => col.statuses.includes(t.status));

  const discardedTickets = visibleTickets.filter(
    (t) => t.status === 'discarded',
  );

  const lastUpdatedLabel = lastUpdated
    ? `Actualizado ${lastUpdated.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`
    : '';

  return (
    <>
      <div className={styles.boardToolbar}>
        <button
          className={`${styles.refreshBtn} ${
            refreshing ? styles.refreshBtnSpinning : ''
          }`}
          onClick={handleRefresh}
          disabled={refreshing}
          title="Actualizar tablero"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M23 4v6h-6M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
          </svg>
          {refreshing ? 'Actualizando...' : 'Actualizar'}
        </button>
        {lastUpdatedLabel && (
          <span className={styles.lastUpdated}>
            {lastUpdatedLabel}
          </span>
        )}
      </div>
      {discardedTickets.length > 0 && (
        <div className={styles.boardToolbar}>
          <button
            className={`${styles.discardedToggle} ${
              showDiscarded ? styles.discardedToggleActive : ''
            }`}
            onClick={() => setShowDiscarded((v) => !v)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline
                points={
                  showDiscarded
                    ? '18 15 12 9 6 15'
                    : '6 9 12 15 18 9'
                }
              />
            </svg>
            {showDiscarded
              ? 'Ocultar rechazados'
              : 'Ver rechazados'}
            <span className={styles.discardedCount}>
              {discardedTickets.length}
            </span>
          </button>
        </div>
      )}
      <div className={styles.board}>
        {COLUMNS.map((col) => {
          const colTickets = byColumn(col);
          return (
            <div
              key={col.key}
              className={`${styles.column} ${
                col.highlight ? styles.columnHighlight : ''
              }`}
            >
              <div
                className={styles.colHeader}
                style={
                  col.highlight
                    ? { borderBottomColor: col.color }
                    : {}
                }
              >
                <div className={styles.colTitle}>
                  <span
                    className={styles.colDot}
                    style={{ background: col.color }}
                  />
                  {col.label}
                </div>
                <span
                  className={`${styles.colCount} ${
                    col.highlight && colTickets.length > 0
                      ? styles.colCountBadge
                      : ''
                  }`}
                  style={
                    col.highlight && colTickets.length > 0
                      ? { background: col.color, color: '#fff' }
                      : {}
                  }
                >
                  {colTickets.length}
                </span>
              </div>

              {col.hint && colTickets.length > 0 && (
                <div
                  className={styles.colHint}
                  style={{ color: col.color }}
                >
                  {col.hint}
                </div>
              )}

              <div className={styles.colCards}>
                {colTickets.map((t) => (
                  <TicketCard
                    key={t.id}
                    ticket={t}
                    highlight={!!col.highlight}
                    neoState={neoStates[t.notionId]}
                    onOpen={setSelected}
                  />
                ))}
                {colTickets.length === 0 && (
                  <div className={styles.empty}>Sin tickets</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sección de tickets rechazados */}
      {showDiscarded && discardedTickets.length > 0 && (
        <div className={styles.discardedSection}>
          <div className={styles.discardedHeader}>
            <span className={styles.discardedHeaderIcon}>✕</span>
            Tickets rechazados
            <span className={styles.discardedCount}>
              {discardedTickets.length}
            </span>
          </div>
          <div className={styles.discardedGrid}>
            {discardedTickets.map((t) => (
              <TicketCard
                key={t.id}
                ticket={t}
                highlight={false}
                neoState={undefined}
                onOpen={setSelected}
              />
            ))}
          </div>
        </div>
      )}

      {selected && (
        <TicketDetail
          ticket={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
